import { createSelector } from '@ngrx/store'

import * as cart from '../actions/cart'
import * as auth from '../../auth/actions/auth.actions'
import * as checkout from '../../checkout/actions/checkout'

import { ApiOrder, ApiOrderline, CartProduct } from '../models/cart-models';

import { ABSOLUTE_QUANT, ThemeConfig } from '../../core/helpers/theme-config';

const quantityType = ThemeConfig.get<string>('catalog', 'quantity-type')

export interface State {
    order: ApiOrder,
    cartIndex: string[]
    cartProducts: { [product: string]: CartProduct }
}

const initialState: State = {
    order: null,
    cartIndex: [],
    cartProducts: {}
}

function calculateQuantity(line: ApiOrderline): number {
    if(quantityType === ABSOLUTE_QUANT)
        return line.quantity
    return line.quantity / line.stepping
}

function calculateRelativeQuantity(line: ApiOrderline): number {
  let quantity = line.quantity
  let stepping = line.stepping || 1

  return quantity / stepping
}

export function reducer(
    state = initialState,
    action: cart.Actions | auth.Actions | checkout.Actions
) {
    switch(action.type) {
        case cart.ADD:
            let inCart = (state.cartProducts[action.payload.product]
                && state.cartProducts[action.payload.product].inCart) || false
            let quantity = (state.cartProducts[action.payload.product]
                && state.cartProducts[action.payload.product].quantity) || 0
            let stepping = (state.cartProducts[action.payload.product]
                && state.cartProducts[action.payload.product].stepping) || 1
            let relativeQuantity = (state.cartProducts[action.payload.product]
                && state.cartProducts[action.payload.product].relativeQuantity) || 0

            // Create new cartIndex. Check if item already was in cart (so no duplicates will be in cartIndex)
            let newCartIndex = [...state.cartIndex]
            if (newCartIndex.indexOf(action.payload.product) === -1) {
              newCartIndex.push(action.payload.product)
            }

            return {
                ...state,
                cartIndex: newCartIndex,
                cartProducts: {
                    ...state.cartProducts,
                    [action.payload.product]: {
                        quantity,
                        stepping,
                        relativeQuantity,
                        inCart,
                        busy: true
                    }
                }
            }

        case cart.ADD_SUCCESS:
        case cart.GET_SUCCESS:
            let order = action.payload
            let cartProducts = order.lines.reduce(
                (dict, line) => Object.assign(
                    dict,
                    {
                        [line.product._id]: {
                            quantity: calculateQuantity(line),
                            stepping: line.stepping,
                            relativeQuantity: calculateRelativeQuantity(line),
                            inCart: true,
                            busy: false
                        }
                    }
                ),
                {...state.cartProducts})

            let cartIndex = Object.keys(cartProducts)

            return {
                ...state,
                order,
                cartIndex,
                cartProducts
            }

        case checkout.SET_SUCCESS:
        case cart.CHANGE_SUCCESS:
        case cart.REMOVE_SUCCESS:
            order = action.payload
            let removeProducts = {...state.cartProducts}

            let removed = state.cartIndex.find(indexValue =>
                order.lines.findIndex(line => line.product._id === indexValue) === -1)

            if (removed) delete removeProducts[removed]

            return {
                ...state,
                order,
                cartIndex: Object.keys(removeProducts),
                cartProducts: removeProducts
            }

        case auth.LOGOUT:
        case cart.RESET:
            return initialState

        default:
            return state

    }
}

export const getOrder = (state: State) => state.order
export const getTotalValue = (state: State) => (state.order && state.order.subTotalPrice) || 0
export const getLineCount = (state: State) => (state.order && state.order.totalOrderLines) || 0
export const getBusyProducts = (state: State) => state.cartProducts

export const getCurrency = createSelector(
    getOrder,
    order => order && order.currency
)
export const getProductCount = createSelector(
    getOrder,
    order => {
        if (!order)
            return 0
        return order.lines.reduce((count, line) => count + line.quantity, 0)
    }
)
export const getProductIds = createSelector(
    getOrder,
    order => (order && order.lines.map(line => line.product._id)) || []
)

export const getOrderLines = createSelector(
  getOrder,
  order => order && order.lines || []
)
